import { useQuery } from '@tanstack/react-query';
import { fetchItems } from '../support/queries';

export interface PovActivityCounts {
  /** Open opportunities closing in the period. */
  opportunity_count: number;
  /** Of those, how many have at least one POC/POV SE activity (logged on any date). */
  opps_with_pov_activity_count: number;
  /** POC/POV SE activities on those opportunities. */
  pov_activity_count: number;
}

export interface PovActivityNode {
  email: string;
  is_manager?: boolean;
  /** This person's own figures. */
  own: PovActivityCounts;
  /** This person plus every descendant. */
  rollup: PovActivityCounts;
  reports?: Record<string, PovActivityNode>;
}

export interface PovActivitySummaryResponse {
  report?: string;
  target_email?: string;
  current_time_filter?: string;
  tree?: PovActivityNode;
}

/**
 * Opportunity / POV-activity counts for a leader's org, from `sf/team/pov-activity-summary/`. Opportunities
 * are those closing in `timeFilter` (`all` or `range:YYYY-MM-DD:YYYY-MM-DD`); their POV/POC activities are
 * counted whenever they happened.
 */
export function useSfTeamPovActivitySummary(
  email: string | null | undefined,
  timeFilter: string,
  options: { enabled?: boolean } = {},
) {
  const normalizedEmail = String(email || '').toLowerCase() || null;
  return useQuery<PovActivitySummaryResponse | null>({
    queryKey: ['SfTeamPovActivitySummary', normalizedEmail, timeFilter],
    queryFn: async () => {
      if (!normalizedEmail) return null;
      return fetchItems(
        'sf/team/pov-activity-summary/',
        { email: normalizedEmail, current_time_filter: timeFilter },
        { includePageSize: false },
      );
    },
    enabled: !!normalizedEmail && (options.enabled ?? true),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

/**
 * Figures per direct report (lower-cased email): a manager's are their whole team's (`rollup`), an IC's their
 * own. Null when the tree is missing or malformed, so callers show "—" rather than a false 0.
 */
export function povActivityByReport(
  tree: PovActivityNode | null | undefined,
): Map<string, PovActivityCounts> | null {
  if (!tree) return null;
  const reports = Object.values(tree.reports ?? {});
  const out = new Map<string, PovActivityCounts>();
  for (const r of reports) {
    const c = r.rollup;
    if (!c || typeof c.opportunity_count !== 'number') return null;
    out.set(String(r.email || '').trim().toLowerCase(), {
      opportunity_count: c.opportunity_count,
      opps_with_pov_activity_count: Number(c.opps_with_pov_activity_count ?? 0),
      pov_activity_count: Number(c.pov_activity_count ?? 0),
    });
  }
  return out;
}

export interface PovActivityItem {
  id: string;
  subject?: string | null;
  /** Salesforce ActivityDate, `YYYY-MM-DD`. */
  date?: string | null;
  status?: string | null;
  /** The Task's record page in Salesforce (Lightning); null if the server could not build it. */
  url?: string | null;
  priority?: string | null;
  /** The Task's SE service type (e.g. "POC/POV"). */
  service_type?: string | null;
  duration_days?: number | null;
  technology?: string | null;
  next_step?: string | null;
  /** The Task's free-text description (capped server-side). */
  description?: string | null;
}

export interface PovActivityOpportunity {
  id: string;
  name?: string | null;
  account_id?: string | null;
  account_name?: string | null;
  stage?: string | null;
  amount?: number | null;
  close_date?: string | null;
  /** The person's POC/POV SE activities on this opportunity, newest first (may be empty). */
  activities: PovActivityItem[];
}

export interface PovActivityDetailResponse extends PovActivitySummaryResponse {
  opportunities?: PovActivityOpportunity[];
}

/**
 * One person's opportunities (closing in `timeFilter`) with their POV/POC SE activities listed under each,
 * from `sf/team/pov-activity-summary/?detail=1`. The same response also carries their three counts in `tree`.
 */
export function useSfPovActivityDetail(
  email: string | null | undefined,
  timeFilter: string,
  options: { enabled?: boolean } = {},
) {
  const normalizedEmail = String(email || '').toLowerCase() || null;
  return useQuery<PovActivityDetailResponse | null>({
    queryKey: ['SfPovActivityDetail', normalizedEmail, timeFilter],
    queryFn: async () => {
      if (!normalizedEmail) return null;
      return fetchItems(
        'sf/team/pov-activity-summary/',
        { email: normalizedEmail, current_time_filter: timeFilter, detail: '1' },
        { includePageSize: false },
      );
    },
    enabled: !!normalizedEmail && (options.enabled ?? true),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}
